/**
 * Role-based menu configuration and permission checking functions
 */

import { USER_ROLES } from './constants';
import type { UserRole } from './constants';

export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  path: string;
  roles: UserRole[];
  children?: MenuItem[];
  /** If set, this item is only shown when the school has this feature key active. */
  featureKey?: string;
}

/**
 * Menu configuration with role-based access control
 */
export const MENU_ITEMS: MenuItem[] = [
  {
    key: 'superadmin-dashboard',
    label: 'SuperAdmin Dashboard',
    icon: 'DashboardOutlined',
    path: '/superadmin-dashboard',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    path: '/dashboard',
    roles: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
    ],
  },
  {
    key: 'teacher-dashboard',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    path: '/teacher/dashboard',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'guardian-dashboard',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    path: '/guardian/dashboard',
    roles: [USER_ROLES.PARENT],
  },
  {
    key: 'guardian-messages',
    label: 'Message School',
    icon: 'MessageOutlined',
    path: '/guardian/messages',
    roles: [USER_ROLES.PARENT],
  },
  {
    key: 'guardian-fleet-feedback',
    label: 'Transport Feedback',
    icon: 'CarOutlined',
    path: '/guardian/fleet-feedback',
    roles: [USER_ROLES.PARENT],
    featureKey: 'fleet-management',
  },
  {
    key: 'student-dashboard',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    path: '/student/dashboard',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-academics',
    label: 'My Results',
    icon: 'TrophyOutlined',
    path: '/student/academics',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-attendance',
    label: 'My Attendance',
    icon: 'CheckCircleOutlined',
    path: '/student/attendance',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-subjects',
    label: 'My Subjects',
    icon: 'BookOutlined',
    path: '/student/subjects',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-financials',
    label: 'Fees & Payments',
    icon: 'DollarOutlined',
    path: '/student/financials',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-messages',
    label: 'Message School',
    icon: 'MessageOutlined',
    path: '/student/messages',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'student-profile',
    label: 'My Profile',
    icon: 'UserOutlined',
    path: '/student/profile',
    roles: [USER_ROLES.STUDENT],
  },
  {
    key: 'teacher-classes',
    label: 'My Classes',
    icon: 'AppstoreOutlined',
    path: '/teacher/classes',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'teacher-subjects',
    label: 'My Subjects',
    icon: 'BookOutlined',
    path: '/teacher/subjects',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'teacher-students',
    label: 'My Students',
    icon: 'UserOutlined',
    path: '/teacher/students',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'teacher-assessments',
    label: 'Assessments',
    icon: 'FileTextOutlined',
    path: '/teacher/assessments',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'teacher-ecd',
    label: 'ECD Classes',
    icon: 'HeartOutlined',
    path: '/teacher/ecd',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'teacher-attendance',
    label: 'Attendance',
    icon: 'CheckCircleOutlined',
    path: '/teacher/attendance',
    roles: [USER_ROLES.TEACHER],
  },
  {
    key: 'academic',
    label: 'Academic',
    icon: 'AppstoreOutlined',
    path: '/academic',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.RECEPTIONIST],
    children: [
      {
        key: 'classes',
        label: 'Classes',
        icon: 'AppstoreOutlined',
        path: '/classes',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'teachers',
        label: 'Teachers',
        icon: 'TeamOutlined',
        path: '/teachers',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'students',
        label: 'Students',
        icon: 'UserOutlined',
        path: '/students',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.RECEPTIONIST],
      },
      {
        key: 'ecd-overview',
        label: 'ECD Overview',
        icon: 'HeartOutlined',
        path: '/admin/ecd',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
    ],
  },
  {
    key: 'curriculum',
    label: 'Curriculum',
    icon: 'BookOutlined',
    path: '/curriculum',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    children: [
      {
        key: 'subjects',
        label: 'Subjects',
        icon: 'BookOutlined',
        path: '/subjects',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'assessments',
        label: 'Assessments',
        icon: 'FileTextOutlined',
        path: '/assessments',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'results',
        label: 'Results',
        icon: 'TrophyOutlined',
        path: '/results',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
    ],
  },
  {
    key: 'financials',
    label: 'Financials',
    icon: 'DollarOutlined',
    path: '/financials',
    roles: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.ACCOUNTS_CLERK,
      USER_ROLES.RECEPTIONIST,
    ],
    children: [
      {
        key: 'financial-dashboard',
        label: 'Financial Dashboard',
        icon: 'DashboardOutlined',
        path: '/dashboard',
        roles: [USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'charge-items',
        label: 'Fees & Levies',
        icon: 'OrderedListOutlined',
        path: '/charges/items',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'payments',
        label: 'Payments & Arrears',
        icon: 'DollarOutlined',
        path: '/payments',
        roles: [
          USER_ROLES.SCHOOL_ADMIN,
          USER_ROLES.PRINCIPAL,
          USER_ROLES.FINANCE_OFFICER,
          USER_ROLES.ACCOUNTS_CLERK,
          USER_ROLES.RECEPTIONIST,
        ],
      },
      {
        key: 'expenses',
        label: 'Expenses',
        icon: 'ShoppingOutlined',
        path: '/expenses',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER, USER_ROLES.ACCOUNTS_CLERK, USER_ROLES.RECEPTIONIST],
      },
      {
        key: 'expense-categories',
        label: 'Expense Categories',
        icon: 'AppstoreOutlined',
        path: '/expense-categories',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER, USER_ROLES.RECEPTIONIST],
      },
      {
        key: 'reports',
        label: 'Financial Reports',
        icon: 'BarChartOutlined',
        path: '/reports',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
      },
    ],
  },
  {
    key: 'accounting',
    label: 'Accounting',
    icon: 'AccountBookOutlined',
    path: '/accounting',
    featureKey: 'accounting',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    children: [
      {
        key: 'accounting-overview',
        label: 'Overview',
        icon: 'DashboardOutlined',
        path: '/accounting',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'chart-of-accounts',
        label: 'Chart of Accounts',
        icon: 'UnorderedListOutlined',
        path: '/accounting/chart-of-accounts',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'journal-entries',
        label: 'Journal Entries',
        icon: 'FileTextOutlined',
        path: '/accounting/journal-entries',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'general-ledger',
        label: 'General Ledger',
        icon: 'BookOutlined',
        path: '/accounting/general-ledger',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'budgets',
        label: 'Budgets',
        icon: 'FundOutlined',
        path: '/accounting/budgets',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      // {
      //   key: 'payroll',
      //   label: 'Payroll',
      //   icon: 'TeamOutlined',
      //   path: '/accounting/payroll',
      //   featureKey: 'accounting',
      //   roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      // },
      {
        key: 'creditors',
        label: 'Creditors',
        icon: 'BankOutlined',
        path: '/accounting/creditors',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'accounting-reports',
        label: 'Reports',
        icon: 'BarChartOutlined',
        path: '/accounting/reports',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'requisitions',
        label: 'Requisitions',
        icon: 'FileTextOutlined',
        path: '/accounting/requisitions',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'fixed-assets',
        label: 'Fixed Assets',
        icon: 'BankOutlined',
        path: '/accounting/fixed-assets',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'student-invoices',
        label: 'Student Invoices',
        icon: 'OrderedListOutlined',
        path: '/accounting/student-invoices',
        featureKey: 'accounting',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: 'CheckCircleOutlined',
    path: '/operations',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.GUARD, USER_ROLES.RECEPTIONIST],
    children: [
      {
        key: 'attendance',
        label: 'Attendance',
        icon: 'CheckCircleOutlined',
        path: '/attendance',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.GUARD, USER_ROLES.RECEPTIONIST],
      },
    ],
  },
  {
    key: 'communication',
    label: 'Communication',
    icon: 'MessageOutlined',
    path: '/communication',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.SUPER_ADMIN],
    children: [
      {
        key: 'tickets',
        label: 'Messages from Parents',
        icon: 'MessageOutlined',
        path: '/tickets',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'send-message',
        label: 'Send Message',
        icon: 'SendOutlined',
        path: '/communication/send',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'message-history',
        label: 'Message History',
        icon: 'HistoryOutlined',
        path: '/communication/history',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'templates',
        label: 'Templates',
        icon: 'FileTextOutlined',
        path: '/communication/templates',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'scheduled-messages',
        label: 'Scheduled Messages',
        icon: 'ClockCircleOutlined',
        path: '/communication/scheduled',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'credit-balance',
        label: 'Credit Balance',
        icon: 'WalletOutlined',
        path: '/communication/credits',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'school-credits',
        label: 'School Credits',
        icon: 'WalletOutlined',
        path: '/communication/school-credits',
        roles: [USER_ROLES.SUPER_ADMIN],
      },
      {
        key: 'credit-topup',
        label: 'Credit Top-Up',
        icon: 'DollarOutlined',
        path: '/communication/credit-topup',
        roles: [USER_ROLES.SUPER_ADMIN],
      },
      {
        key: 'credit-pricing',
        label: 'Credit Pricing',
        icon: 'DollarOutlined',
        path: '/communication/credit-pricing',
        roles: [USER_ROLES.SUPER_ADMIN],
      },
      {
        key: 'all-transactions',
        label: 'All Transactions',
        icon: 'HistoryOutlined',
        path: '/communication/all-transactions',
        roles: [USER_ROLES.SUPER_ADMIN],
      },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: 'SettingOutlined',
    path: '/settings',
    roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    children: [
      {
        key: 'users',
        label: 'Manage Users',
        icon: 'TeamOutlined',
        path: '/users',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_ADMIN],
      },
      {
        key: 'grading-scales',
        label: 'Grading Scales',
        icon: 'PercentageOutlined',
        path: '/grading-scales',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'promotions',
        label: 'Promotions',
        icon: 'RiseOutlined',
        path: '/promotions',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'academic-settings',
        label: 'Academic Settings',
        icon: 'SettingOutlined',
        path: '/academic-settings',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
      {
        key: 'add-ons',
        label: 'Add-Ons',
        icon: 'AppstoreAddOutlined',
        path: '/add-ons',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
      },
    ],
  },
  {
    key: 'fleet',
    label: 'Fleet Management',
    icon: 'CarOutlined',
    path: '/fleet',
    featureKey: 'fleet-management',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
    children: [
      {
        key: 'fleet-dashboard',
        label: 'Fleet Dashboard',
        icon: 'DashboardOutlined',
        path: '/fleet/dashboard',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-vehicles',
        label: 'Vehicles',
        icon: 'CarOutlined',
        path: '/fleet/vehicles',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-drivers',
        label: 'Drivers',
        icon: 'TeamOutlined',
        path: '/fleet/drivers',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-routes',
        label: 'Routes',
        icon: 'NodeIndexOutlined',
        path: '/fleet/routes',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-schedules',
        label: 'Schedules',
        icon: 'CalendarOutlined',
        path: '/fleet/schedules',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-checklists',
        label: 'Checklists',
        icon: 'CheckSquareOutlined',
        path: '/fleet/checklists',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-requisitions',
        label: 'Fleet Requests',
        icon: 'FileTextOutlined',
        path: '/fleet/requisitions',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-hire-out',
        label: 'Hire-Out',
        icon: 'SwapOutlined',
        path: '/fleet/hire-out',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
      {
        key: 'fleet-feedback',
        label: 'Parent Feedback',
        icon: 'MessageOutlined',
        path: '/fleet/feedback',
        featureKey: 'fleet-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FLEET_MANAGER],
      },
    ],
  },
  {
    key: 'driver-dashboard',
    label: 'Dashboard',
    icon: 'DashboardOutlined',
    path: '/driver/dashboard',
    roles: [USER_ROLES.DRIVER, USER_ROLES.FLEET_MANAGER],
  },
  {
    key: 'driver-schedules',
    label: 'My Schedules',
    icon: 'CalendarOutlined',
    path: '/driver/schedules',
    roles: [USER_ROLES.DRIVER, USER_ROLES.FLEET_MANAGER],
  },
  {
    key: 'driver-checklist',
    label: 'Pre-Trip Checklist',
    icon: 'CheckSquareOutlined',
    path: '/driver/checklist',
    roles: [USER_ROLES.DRIVER, USER_ROLES.FLEET_MANAGER],
  },
  {
    key: 'driver-requisitions',
    label: 'My Requests',
    icon: 'FileTextOutlined',
    path: '/driver/requisitions',
    roles: [USER_ROLES.DRIVER, USER_ROLES.FLEET_MANAGER],
  },
  {
    key: 'meetings',
    label: 'Meeting Recordings',
    icon: 'AudioOutlined',
    path: '/meetings',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    featureKey: 'meeting-services',
  },
  {
    key: 'inventory',
    label: 'Inventory',
    icon: 'AppstoreOutlined',
    path: '/inventory',
    featureKey: 'inventory-management',
    roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER, USER_ROLES.RECEPTIONIST],
    children: [
      {
        key: 'inventory-overview',
        label: 'Overview',
        icon: 'DashboardOutlined',
        path: '/inventory',
        featureKey: 'inventory-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'inventory-categories',
        label: 'Categories',
        icon: 'TagsOutlined',
        path: '/inventory/categories',
        featureKey: 'inventory-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
      },
      {
        key: 'inventory-items',
        label: 'Items',
        icon: 'UnorderedListOutlined',
        path: '/inventory/items',
        featureKey: 'inventory-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER, USER_ROLES.RECEPTIONIST],
      },
      {
        key: 'inventory-collections',
        label: 'Ready to Collect',
        icon: 'CheckCircleOutlined',
        path: '/inventory/collections',
        featureKey: 'inventory-management',
        roles: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER, USER_ROLES.RECEPTIONIST],
      },
    ],
  },
  {
    key: 'schools',
    label: 'Schools',
    icon: 'BankOutlined',
    path: '/schools',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'blog-posts',
    label: 'News & Blog',
    icon: 'FileTextOutlined',
    path: '/blog-posts',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'admin-announcements',
    label: 'Announcements',
    icon: 'NotificationOutlined',
    path: '/admin-announcements',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'academic-years',
    label: 'Academic Years',
    icon: 'CalendarOutlined',
    path: '/academic-years',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: 'CreditCardOutlined',
    path: '/billing',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'platform-payments',
    label: 'Platform Payments',
    icon: 'DollarOutlined',
    path: '/platform-payments',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'platform-support',
    label: 'Support Contacts',
    icon: 'CustomerServiceOutlined',
    path: '/platform-support',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
  {
    key: 'system-diagnostics',
    label: 'System Diagnostics',
    icon: 'ToolOutlined',
    path: '/system-diagnostics',
    roles: [USER_ROLES.SUPER_ADMIN],
  },
];

/**
 * Get menu items accessible to a specific user role
 * @param userRole - User role to check
 * @returns Array of menu items accessible to the role
 */
export const getMenuItemsForRole = (userRole: UserRole): MenuItem[] => {
  return MENU_ITEMS.map((item) => {
    // Create a copy to avoid mutating original
    const itemCopy = { ...item };

    // If item has children, filter them based on role
    if (itemCopy.children) {
      const accessibleChildren = itemCopy.children.filter((child) =>
        child.roles.includes(userRole)
      );
      // Only include parent if user has access to at least one child
      if (accessibleChildren.length === 0) {
        return null;
      }
      // Update children to only include accessible ones
      itemCopy.children = accessibleChildren;
      return itemCopy;
    }

    // Check if user has access to this menu item (for items without children)
    if (!itemCopy.roles.includes(userRole)) {
      return null;
    }

    return itemCopy;
  }).filter((item): item is MenuItem => item !== null);
};

/**
 * Check if a user role has permission to access a specific route
 * @param userRole - User role to check
 * @param path - Route path to check
 * @returns True if the role has access, false otherwise
 */
export const hasRoutePermission = (userRole: UserRole, path: string): boolean => {
  // Check top-level menu items
  const menuItem = MENU_ITEMS.find((item) => item.path === path);
  if (menuItem) {
    return menuItem.roles.includes(userRole);
  }

  // Check child menu items
  for (const item of MENU_ITEMS) {
    if (item.children) {
      const childItem = item.children.find((child) => child.path === path);
      if (childItem) {
        return childItem.roles.includes(userRole);
      }
    }
  }

  return false;
};

/**
 * Check if a user role has permission for a specific action
 * @param userRole - User role to check
 * @param action - Action to check (e.g., 'create_student', 'approve_expense')
 * @returns True if the role has permission, false otherwise
 */
export const hasPermission = (userRole: UserRole, action: string): boolean => {
  const permissions: Record<string, UserRole[]> = {
    // Student permissions
    create_student: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.RECEPTIONIST],
    edit_student: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.RECEPTIONIST],
    view_student: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.RECEPTIONIST],
    // Removing a pupil (mark as left, or delete) - the roles the API allows for both
    delete_student: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    reactivate_student: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.RECEPTIONIST],
    bulk_upload_students: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.RECEPTIONIST],

    // Payment permissions
    create_payment: [
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.ACCOUNTS_CLERK,
      USER_ROLES.RECEPTIONIST,
    ],
    view_payment: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.ACCOUNTS_CLERK,
      USER_ROLES.RECEPTIONIST,
      USER_ROLES.PRINCIPAL,
    ],
    download_receipt: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.ACCOUNTS_CLERK,
      USER_ROLES.RECEPTIONIST,
    ],

    // Attendance permissions
    scan_attendance: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.GUARD, USER_ROLES.RECEPTIONIST],
    view_attendance: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.GUARD, USER_ROLES.RECEPTIONIST],
    manual_attendance: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.GUARD, USER_ROLES.RECEPTIONIST],
    view_attendance_report: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Expense permissions
    create_expense: [USER_ROLES.FINANCE_OFFICER, USER_ROLES.ACCOUNTS_CLERK, USER_ROLES.RECEPTIONIST],
    view_expense: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER, USER_ROLES.ACCOUNTS_CLERK, USER_ROLES.RECEPTIONIST],
    approve_expense: [USER_ROLES.SCHOOL_ADMIN],

    // Void a posted financial record (expense, payment, fulfilled requisition).
    // Separation of duties: the clerks who record cannot void.
    void_financial_record: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],

    // Accounting permissions (advanced-accounting feature)
    view_accounting: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    manage_chart_of_accounts: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    create_journal_entry: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    post_journal_entry: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    approve_budget: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    manage_creditors: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    record_creditor_invoice: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    create_payroll: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],
    post_payroll: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.FINANCE_OFFICER],

    // User management permissions
    create_user: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_ADMIN],
    edit_user: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_ADMIN],
    deactivate_user: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_ADMIN],

    // Report permissions
    view_dashboard: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.RECEPTIONIST,
      USER_ROLES.GUARD,
    ],
    view_financial_report: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
    ],
    export_report: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],

    // School management permissions (SuperAdmin only)
    create_school: [USER_ROLES.SUPER_ADMIN],
    edit_school: [USER_ROLES.SUPER_ADMIN],
    suspend_school: [USER_ROLES.SUPER_ADMIN],
    view_schools: [USER_ROLES.SUPER_ADMIN],

    // Billing permissions (SuperAdmin only)
    view_billing: [USER_ROLES.SUPER_ADMIN],
    manage_billing: [USER_ROLES.SUPER_ADMIN],
    record_billing_payment: [USER_ROLES.SUPER_ADMIN],

    // Teacher permissions
    create_teacher: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    edit_teacher: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    view_teacher: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    deactivate_teacher: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    assign_teacher: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Guardian permissions
    create_guardian: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    edit_guardian: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    view_guardian: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    deactivate_guardian: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    link_guardian: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Subject permissions
    create_subject: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    edit_subject: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    view_subject: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    load_curriculum: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    assign_subject: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    enroll_subject: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Assessment permissions
    create_assessment: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],
    edit_assessment: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],
    view_assessment: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],
    delete_assessment: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],

    // Result permissions
    upload_marks: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],
    view_results: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],
    publish_results: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    download_report: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.TEACHER],

    // Grading permissions
    manage_grading: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Promotion permissions
    promote_students: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
    view_promotion_history: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Academic Settings permissions
    manage_academic_settings: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],

    // Inventory permissions (inventory-management feature).
    // Configuring the catalogue - items, categories, variants, levy links, stock counts - is a
    // back-office job. Handing goods over the counter is not, so Receptionist gets the second
    // permission without the first.
    manage_inventory: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL, USER_ROLES.FINANCE_OFFICER],
    issue_inventory: [
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.RECEPTIONIST,
    ],
    // Bulk assign can raise a charge against a whole class, so it stays narrower than the rest.
    bulk_assign_inventory: [USER_ROLES.SCHOOL_ADMIN, USER_ROLES.PRINCIPAL],
  };

  const allowedRoles = permissions[action];
  if (!allowedRoles) return false;

  return allowedRoles.includes(userRole);
};

/**
 * Check if a user role is SuperAdmin
 * @param userRole - User role to check
 * @returns True if SuperAdmin, false otherwise
 */
export const isSuperAdmin = (userRole: UserRole): boolean => {
  return userRole === USER_ROLES.SUPER_ADMIN;
};

/**
 * Check if a user role is SchoolAdmin
 * @param userRole - User role to check
 * @returns True if SchoolAdmin, false otherwise
 */
export const isSchoolAdmin = (userRole: UserRole): boolean => {
  return userRole === USER_ROLES.SCHOOL_ADMIN;
};

/**
 * Get allowed roles for user creation based on current user role
 * @param userRole - Current user role
 * @returns Array of roles that can be assigned
 */
export const getAllowedRolesForCreation = (userRole: UserRole): UserRole[] => {
  if (userRole === USER_ROLES.SUPER_ADMIN) {
    return [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.SCHOOL_ADMIN,
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.RECEPTIONIST,
      USER_ROLES.GUARD,
    ];
  }

  if (userRole === USER_ROLES.SCHOOL_ADMIN) {
    // SchoolAdmin cannot create other SchoolAdmins
    return [
      USER_ROLES.PRINCIPAL,
      USER_ROLES.FINANCE_OFFICER,
      USER_ROLES.ACCOUNTS_CLERK,
      USER_ROLES.RECEPTIONIST,
      USER_ROLES.GUARD,
      USER_ROLES.FLEET_MANAGER,
      USER_ROLES.DRIVER,
    ];
  }

  return [];
};

/**
 * Get the default route for a user role
 * Different roles are redirected to their primary work area
 * @param userRole - User role to check
 * @returns Default route path for the role
 */
export const getDefaultRouteForRole = (userRole: UserRole): string => {
  if (userRole === USER_ROLES.SUPER_ADMIN) {
    return '/superadmin-dashboard';
  }
  if (userRole === USER_ROLES.GUARD) {
    return '/attendance';
  }
  if (userRole === USER_ROLES.FINANCE_OFFICER) {
    return '/dashboard';
  }
  if (userRole === USER_ROLES.ACCOUNTS_CLERK) {
    return '/payments';
  }
  if (userRole === USER_ROLES.RECEPTIONIST) {
    return '/students';
  }
  if (userRole === USER_ROLES.TEACHER) {
    return '/teacher/dashboard';
  }
  if (userRole === USER_ROLES.PARENT) {
    return '/guardian/dashboard';
  }
  if (userRole === USER_ROLES.STUDENT) {
    return '/student/dashboard';
  }
  if (userRole === USER_ROLES.FLEET_MANAGER) {
    return '/fleet/dashboard';
  }
  if (userRole === USER_ROLES.DRIVER) {
    return '/driver/dashboard';
  }
  return '/dashboard';
};
